import React, { useState } from "react";
import { MessageSquare, ArrowUpRight, Star, ChevronDown, ChevronUp, Lock } from "lucide-react";
import type { DataRecord, SocialQueueItem } from "../../types";
import { SocialBadge } from "./SocialBadge";
import { formatDateShort } from "./socialFormat";

interface Props {
  row: DataRecord & { _isReplyRow?: boolean; _replyCount?: number; _hasOwnerReply?: boolean };
  replies?: DataRecord[];
  clientSlug: string;
  userCanReply: boolean;
  isModuleConfigured: boolean;
  onOpenReply: (item: SocialQueueItem) => void;
  onOpenDetailModal?: (row: DataRecord) => void;
  buildActionLink: (row: DataRecord) => { href: string; label: string } | null;
}

export function SocialCommentMobileCard({
  row,
  replies = [],
  clientSlug,
  userCanReply,
  isModuleConfigured,
  onOpenReply,
  onOpenDetailModal,
  buildActionLink,
}: Props) {
  const [showReplies, setShowReplies] = useState(false);
  const text = (v: any, fallback = "") => (v !== null && v !== undefined ? String(v) : fallback);
  const number = (v: any, fallback = 0) => {
    const n = Number(v);
    return isNaN(n) ? fallback : n;
  };
  const shortDateLabel = formatDateShort;

  const plat = text(row.platform).toLowerCase();
  const isGbp = plat === "gbp" || plat.includes("google");
  const isIg = plat === "meta_ig" || plat.includes("ig") || plat.includes("instagram");
  const isFb = plat === "meta_fb" || plat.includes("fb") || plat.includes("facebook");
  const isPaid = text(row.origin).toLowerCase() === "paid";
  const isOwner = Boolean(row.is_owner_author);
  const isReplied = Boolean(row.has_reply) || Boolean(row._hasOwnerReply);
  const rating = row.star_rating !== null && row.star_rating !== undefined ? number(row.star_rating) : null;
  const link = buildActionLink(row);
  const rawMsg = text(row.message);
  const isLong = rawMsg.length > 150;

  const platformColor = isGbp ? "#34a853" : isIg ? "#e1306c" : isFb ? "#1877f2" : "var(--blue, #7d96ff)";
  const platformLabel = isGbp ? "Google BP" : isIg ? "Instagram" : isFb ? "Facebook" : "Red Social";

  const queueItem: SocialQueueItem = {
    feedback_id: text(row.feedback_id),
    date: text(row.date),
    created_time: text(row.created_time || row.date),
    platform: text(row.platform),
    origin: text(row.origin, "organic"),
    author_name: text(row.author_name || row.author, "Anónimo"),
    message: rawMsg,
    star_rating: rating,
    has_reply: isReplied,
    post_id: text(row.post_id) || null,
    ad_account_id: text(row.ad_account_id) || null,
    page_id: text(row.page_id) || null,
    action_link: link,
    polaridad: text(row.polaridad) || null,
    acto_de_habla: text(row.acto_de_habla) || null,
    aspecto: text(row.aspecto) || null,
    plan: {
      action: "reply",
      priority: 2,
      reason: null,
      template_key: null,
      draft_text: null,
    },
  };

  const getSentimentTone = (pol: string) => {
    const p = pol.toLowerCase();
    if (p.includes("pos")) return "success";
    if (p.includes("neg")) return "danger";
    return "neutral";
  };

  return (
    <div
      className="social-mobile-card"
      style={{
        background: "var(--surface)",
        border: "1px solid var(--line)",
        borderRadius: "12px",
        padding: "14px",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}
    >
      {/* Cabecera de la tarjeta: Plataforma, Rating y Fecha */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "11px",
              fontWeight: 700,
              color: platformColor,
              background: `color-mix(in srgb, ${platformColor} 12%, transparent)`,
              padding: "2px 8px",
              borderRadius: "999px",
            }}
          >
            <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: platformColor }} />
            {platformLabel}
          </span>
          {!isGbp && (
            <span style={{ fontSize: "10px", color: "var(--muted)", background: "var(--surface-2)", padding: "2px 6px", borderRadius: "4px" }}>
              {isPaid ? "Anuncios" : "Orgánico"}
            </span>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "11px", color: "var(--muted)" }}>
          {rating !== null && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "2px", color: "#f59e0b", fontWeight: 700 }}>
              <Star size={12} fill="#f59e0b" />
              {rating.toFixed(1)}
            </span>
          )}
          <span>{shortDateLabel(text(row.created_time || row.date))}</span>
        </div>
      </div>

      {/* Fila del autor y estado de respuesta */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
        <span
          style={{
            fontSize: "13px",
            fontWeight: 700,
            color: isOwner ? "var(--blue)" : "var(--text)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {isOwner ? "👑 Cuenta oficial" : text(row.author_name || row.author, "Anónimo")}
        </span>

        {!isOwner && (
          isReplied ? (
            <span style={{ fontSize: "10.5px", color: "var(--green)", fontWeight: 650 }}>
              ✓ Respondido
            </span>
          ) : (
            <span className="social-badge-unanswered" style={{ fontSize: "10.5px", padding: "1px 6px" }}>
              Sin responder
            </span>
          )
        )}
      </div>

      {/* Contenido del comentario */}
      <div style={{ fontSize: "13px", lineHeight: "1.5", color: "var(--text)", wordBreak: "break-word" }}>
        {isLong ? (
          <>
            {rawMsg.slice(0, 140)}...{" "}
            <button
              type="button"
              onClick={() => onOpenDetailModal && onOpenDetailModal(row)}
              style={{
                background: "transparent",
                border: "none",
                color: "var(--blue)",
                fontWeight: 650,
                cursor: "pointer",
                padding: 0,
                fontSize: "12px",
                display: "inline",
              }}
            >
              Ver completo
            </button>
          </>
        ) : (
          rawMsg || <span style={{ color: "var(--muted)", fontStyle: "italic" }}>Sin texto (solo reacción)</span>
        )}
      </div>

      {/* Chips de metadatos: Sentimiento y Aspecto */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center", paddingTop: "2px" }}>
        {Boolean(row.polaridad) && (
          <SocialBadge tone={getSentimentTone(text(row.polaridad))}>
            {text(row.polaridad)}
          </SocialBadge>
        )}
        {Boolean(row.aspecto) && (
          <span
            style={{
              fontSize: "10.5px",
              padding: "1px 6px",
              borderRadius: "4px",
              background: "var(--surface-2)",
              color: "var(--muted)",
              border: "1px solid var(--line)",
            }}
          >
            {text(row.aspecto)}
          </span>
        )}
      </div>

      {/* Respuestas anidadas (hilo oficial) */}
      {replies.length > 0 && (
        <div style={{ borderTop: "1px dashed var(--line)", paddingTop: "8px" }}>
          <button
            type="button"
            onClick={() => setShowReplies((v) => !v)}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--blue)",
              fontSize: "11.5px",
              fontWeight: 600,
              cursor: "pointer",
              padding: 0,
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            {showReplies ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            <span>{showReplies ? "Ocultar" : "Ver"} {replies.length} {replies.length === 1 ? "respuesta oficial" : "respuestas"}</span>
          </button>

          {showReplies && (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "8px", paddingLeft: "12px", borderLeft: "2px solid var(--blue)" }}>
              {replies.map((rep, rIdx) => (
                <div key={String(rep.feedback_id || rIdx)} style={{ fontSize: "12px", color: "var(--text)" }}>
                  <div style={{ fontWeight: 650, color: "var(--blue)", marginBottom: "2px", fontSize: "11px" }}>
                    ↳ {text(rep.author_name, "Cuenta oficial")} ({shortDateLabel(text(rep.created_time || rep.date))})
                  </div>
                  <div>{text(rep.message)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Barra de acciones táctiles inferiores */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderTop: "1px solid var(--line)",
          paddingTop: "10px",
          marginTop: "4px",
          gap: "8px",
        }}
      >
        {link ? (
          <a
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: "12px",
              color: "var(--muted)",
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              fontWeight: 600,
            }}
          >
            <span>{isGbp ? "Google Maps" : isIg ? "Instagram" : "Facebook"}</span>
            <ArrowUpRight size={13} />
          </a>
        ) : <div />}

        {!userCanReply || !isModuleConfigured ? (
          <button
            type="button"
            className="button button-ghost button-sm"
            disabled
            style={{
              fontSize: "11.5px",
              padding: "4px 10px",
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              opacity: 0.5,
              cursor: "not-allowed",
              color: "var(--muted)",
            }}
            title={!isModuleConfigured ? "Módulo de respuesta no habilitado" : "Usuario no autorizado"}
          >
            <Lock size={12} />
            <span>Responder</span>
          </button>
        ) : (
          <button
            type="button"
            className="button button-primary button-sm"
            onClick={() => onOpenReply(queueItem)}
            style={{
              fontSize: "12px",
              padding: "5px 12px",
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              fontWeight: 650,
              background: platformColor,
              borderColor: platformColor,
              color: "#ffffff",
              cursor: "pointer",
            }}
          >
            <MessageSquare size={13} />
            <span>Responder</span>
          </button>
        )}
      </div>
    </div>
  );
}
